import { Timestamp, deleteField, doc, getDoc, updateDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import type { TonyteActivity } from '../types';
import { getEventOwnerUid, hasPartnerCreatorRole } from './weeklyFeatured';

// Admin-side writes for "Featured This Week". Firestore rules restrict every
// featured* field to admin/co-admin, so these can only succeed for them; the
// checks here just give a clear message instead of a permission error.

export type FeaturedSettings = {
  start: Date;
  end: Date;
  // Lower shows first.
  priority: number;
};

export type FeaturedValidationError = 'endBeforeStart' | 'alreadyEnded' | 'invalidPriority' | 'invalidDate';

export function validateFeaturedSettings(settings: FeaturedSettings, now: Date = new Date()): FeaturedValidationError | null {
  if (Number.isNaN(settings.start.getTime()) || Number.isNaN(settings.end.getTime())) return 'invalidDate';
  if (settings.end.getTime() <= settings.start.getTime()) return 'endBeforeStart';
  if (settings.end.getTime() < now.getTime()) return 'alreadyEnded';
  if (!Number.isInteger(settings.priority) || settings.priority < 1 || settings.priority > 999) return 'invalidPriority';
  return null;
}

// 'YYYY-MM-DD' -> local midnight (start) or 23:59:59.999 (end of that day), so a
// week typed as Mon..Sun covers both whole days.
export function parseFeaturedDateInput(value: string, endOfDay: boolean): Date | null {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
  // Reject rollover such as 2026-02-31 -> March 3.
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export type PartnerEligibility = 'partner' | 'not_partner' | 'unknown';

// Featuring is meant for verified partner events. The Partner Dashboard stamps
// createdByRole: 'partner' on new events; older events are verified against
// the owner's partnerProfiles document (readable by admins) instead.
export async function resolvePartnerEligibility(activity: TonyteActivity): Promise<PartnerEligibility> {
  if (hasPartnerCreatorRole(activity)) return 'partner';
  const ownerUid = getEventOwnerUid(activity);
  if (!ownerUid) return 'not_partner';
  try {
    const snapshot = await getDoc(doc(firestore, 'partnerProfiles', ownerUid));
    if (!snapshot.exists()) return 'not_partner';
    return snapshot.data()?.status === 'active' ? 'partner' : 'not_partner';
  } catch {
    return 'unknown';
  }
}

export async function setWeeklyFeatured(activity: TonyteActivity, adminUid: string, settings: FeaturedSettings) {
  await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.activities, activity.id), {
    isWeeklyFeatured: true,
    featuredStartAt: Timestamp.fromDate(settings.start),
    featuredEndAt: Timestamp.fromDate(settings.end),
    featuredPriority: settings.priority,
    featuredBy: adminUid,
  });
}

// Unfeaturing drops the window and priority (so a stale window can't
// resurrect the flag) but records who did it.
export async function clearWeeklyFeatured(activity: TonyteActivity, adminUid: string) {
  await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.activities, activity.id), {
    isWeeklyFeatured: false,
    featuredStartAt: deleteField(),
    featuredEndAt: deleteField(),
    featuredPriority: deleteField(),
    featuredBy: adminUid,
  });
}

// The same change as an in-memory patch, for the optimistic feed-cache update.
export function featuredPatch(settings: FeaturedSettings | null, adminUid: string): Partial<TonyteActivity> {
  return settings
    ? {
        isWeeklyFeatured: true,
        featuredStartAt: settings.start.toISOString(),
        featuredEndAt: settings.end.toISOString(),
        featuredPriority: settings.priority,
        featuredBy: adminUid,
      }
    : {
        isWeeklyFeatured: false,
        featuredStartAt: undefined,
        featuredEndAt: undefined,
        featuredPriority: undefined,
        featuredBy: adminUid,
      };
}
